import HeroSection from 'components/organism/Generic/HeroSection';
import AlertsWidget from 'components/organism/Generic/AlertsWidget'
import FooterPrompt from 'components/organism/Generic/FooterPrompt';
import SectionHeader from 'components/organism/Generic/SectionHeader';
import { KpiCard, KpiCardSkeleton } from 'components/molecules/Generic/KpiCard';
import { forwardRef, useImperativeHandle } from 'react';

import { ActivityFeedWidget } from 'components/organism/Generic/ActivityFeedWidget';
import { WorkflowCardData, WorkflowCardsGrid } from 'components/organism/Generic/WorkflowCardsGrid';
import Loader from 'components/molecules/Loader';
import { useFetchKpi } from 'hooks/ppa/useFetchKpi';
import { useFetchDashboard } from 'hooks/ppa/useFetchDashboard';
import { useFetchWorkflowCards } from 'hooks/ppa/useFetchWorkflowCards';
import type { KpiDataMerged } from 'utils/ppaHelpers';
import ScopeRibbon from './ScopeRibbon';
import {
    PPA_HOME_ACTIVITY,
    PPA_HOME_ALERTS,
    PPA_HOME_FOOTER_PROMPT,
    PPA_HOME_HERO,
    PPA_HOME_KPI_SECTION,
    PPA_HOME_KPI_SKELETON,
    PPA_HOME_LOADING,
    PPA_HOME_PAGE,
    PPA_HOME_WORKFLOWS_GRID,
    PPA_HOME_WORKFLOWS_LOADING,
    PPA_HOME_WORKFLOWS_SECTION,
    PPA_HOME_KPI_TEST_ID_PREFIX,
    ppaActivityTabTestId,
    ppaKpiCardTestId,
    ppaKpiCardTrendTestId,
    ppaKpiCardValueTestId,
} from 'constants/ppaTestIds';

export const PPADashboard = () => {
    const footerPromptText = 'Have a question about your portfolio or price pack plan?';

    const { dashboardData, isLoading, isPending } = useFetchDashboard();

    if (isLoading || isPending) {
        return (
            <div
                className="flex w-full bg-white"
                style={{ minHeight: 'calc(100vh - 100px)' }}
                data-testid={PPA_HOME_LOADING}
            >
                <Loader size="default" />
            </div>
        );
    }

    const title = dashboardData?.title;
    const subtitle = dashboardData?.subtitle;
    const kpiConfig = dashboardData?.kpis || [];
    const workflowConfig = dashboardData?.workflows || [];
    const scopeConfig = dashboardData?.scope_picker || undefined;

    return (
        <div className="relative min-h-screen bg-white">
            <main className="mx-auto flex w-full flex-col gap-4 p-4" data-testid={PPA_HOME_PAGE}>
                <HeroSection title={title} subtitle={subtitle} data-testid={PPA_HOME_HERO} />
                <ScopeRibbon scopeConfig={scopeConfig} />
                <div className="flex gap-4" data-testid={PPA_HOME_KPI_SECTION}>
                    <KpiCardsContainer config={kpiConfig} />
                </div>
                <div className="flex flex-row items-stretch gap-4">
                    <div className="flex min-w-[320px] flex-1 flex-col">
                        <AlertsContainer />
                    </div>
                    <div className="flex min-w-[320px] flex-1 flex-col">
                        <ActivityFeedContainer />
                    </div>
                </div>
                <SectionHeader
                    title="Deep-dive Workflows"
                    subtitle="Move from insight to action across the full price pack lifecycle."
                    data-testid={PPA_HOME_WORKFLOWS_SECTION}
                />
                <WorkflowCardsContainer config={workflowConfig} />
                <FooterPrompt promptText={footerPromptText} data-testid={PPA_HOME_FOOTER_PROMPT} />
            </main>
        </div>
    );
};

export default PPADashboard;


export type KpiCardsExportHandle = {
    getExportData: () => KpiDataMerged[];
};

export const KpiCardsContainer = forwardRef<
    KpiCardsExportHandle,
    { config: any[]; testIdPrefix?: string }
>(({ config, testIdPrefix = PPA_HOME_KPI_TEST_ID_PREFIX }, ref) => {
    const { kpiData, isLoading, isPending, isScopeSelected } = useFetchKpi(config);
    const noScopeKpiProps = {
        value: '-',
        rawValue: '-',
        valuePrefix: undefined,
        valueSuffix: undefined,
        showTrend: false,
        trendValue: undefined,
        trendType: 'neutral' as const,
    };

    useImperativeHandle(
        ref,
        () => ({
            getExportData: () => kpiData,
        }),
        [kpiData]
    );

    if (isLoading || isPending) {
        const count = config?.length;
        return (
            <>
                {Array.from({ length: count }).map((_, index) => (
                    <KpiCardSkeleton
                        key={index}
                        data-testid={
                            index === 0 ? PPA_HOME_KPI_SKELETON : `${PPA_HOME_KPI_SKELETON}_${index}`
                        }
                    />
                ))}
            </>
        );
    }

    const visibleKpis = kpiData.filter((kpi) => kpi.show !== false);

    return (
        <>
            {visibleKpis.map((kpi) => {
                const scopedKpiProps = isScopeSelected
                    ? {
                        value: kpi.value,
                        rawValue: kpi.raw_value,
                        valuePrefix: kpi.valuePrefix,
                        valueSuffix: kpi.valueSuffix,
                        showTrend: kpi.showTrend,
                        trendValue: kpi.growth_percentage != null ? `${kpi.growth_percentage}%` : undefined,
                        trendType: kpi.trend_direction,
                    }
                    : noScopeKpiProps;

                return (
                    <KpiCard
                        key={kpi.kpi_id}
                        title={kpi.display_label ?? kpi.title ?? ''}
                        data-testid={ppaKpiCardTestId(kpi.kpi_id, testIdPrefix)}
                        valueTestId={ppaKpiCardValueTestId(kpi.kpi_id, testIdPrefix)}
                        trendTestId={ppaKpiCardTrendTestId(kpi.kpi_id, testIdPrefix)}
                        {...scopedKpiProps}
                    />
                );
            })}
        </>
    )
});


const ActivityFeedContainer = () => {
    return (
        <ActivityFeedWidget
            lastVisitedItems={[]}
            bookmarkItems={[]}
            data-testid={PPA_HOME_ACTIVITY}
            getTabTestId={ppaActivityTabTestId}
        />
    );
};

const AlertsContainer = () => {
    return <AlertsWidget title="Total Alerts" data-testid={PPA_HOME_ALERTS} />;
};

const WorkflowCardsContainer = ({ config }: { config: WorkflowCardData[] }) => {
    const { data, isLoading, isPending } = useFetchWorkflowCards(config);

    if (isLoading || isPending) {
        return (
            <div
                className="flex min-w-0 flex-1 flex-col gap-3 rounded-xl bg-backgroundGreyAlt p-4"
                data-testid={PPA_HOME_WORKFLOWS_LOADING}
            >
                <div className="h-4 w-[268px] max-w-full shrink-0 rounded bg-[rgba(0,0,0,0.06)]" />
                <div className="flex min-h-0 flex-1 flex-col items-start justify-center">
                    <div className="flex w-full max-w-[560px] flex-col gap-4">
                        <div className="h-4 w-full shrink-0 rounded bg-[rgba(0,0,0,0.06)]" />
                        <div className="h-4 w-full shrink-0 rounded bg-[rgba(0,0,0,0.06)]" />
                        <div className="h-4 w-full shrink-0 rounded bg-[rgba(0,0,0,0.06)]" />
                    </div>
                </div>
            </div>
        );
    }

    return <WorkflowCardsGrid cards={data} data-testid={PPA_HOME_WORKFLOWS_GRID} />;
}